import React, { useState, useEffect } from "react";
import Paragraph from "@arctheme-components/elements/Paragraph/Paragraph";
import useMobileBreakpoint from '@arctheme-hooks/useMobileBreakpoint';

interface AwardItem {
  title: string;
  description?: string;
  image?: string;
  year?: string;
  category?: string;
  business_unit?: string;
}
interface AwardsProps {
  settings: {
    settings: {
      title?: string;
      description?: string;
      metaobjects?: {
        awards?: {
          entries?: Array<{
            title: string;
            description?: string;
            image?: string;
            year?: string;
            category?: string;
            business_unit?: string;
          }>;
        };
      };
      section?: {
        title?: string;
        description?: string;
      };
      mobile_breakpoint?: number;
    };
  };
}

export const Awards: React.FC<AwardsProps> = ({ settings }) => {
  const { 
    title, 
    description 
  } = settings.settings.section || {};
  
  const isMobile = useMobileBreakpoint(settings.settings.mobile_breakpoint);
  const [isVisible, setIsVisible] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  useEffect(() => {
    // Add a small delay before showing the animation
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  // Extract awards from metaobjects
  const awards: AwardItem[] = settings.settings.awardsData || [];

  const awardTextClass = isMobile ? "b_small_normal" : "b_small_normal";
  const awardYearClass = isMobile ? "b_a1" : "b_a1";
  const awardTitleClass = isMobile ? "h4" : "h5";

  const handleExpand = (index: number) => {
    setExpandedIndex(index === expandedIndex ? null : index);
  };

  return (
    <section className="relative">
      {/* Full page overlay that appears when a tile is expanded */}
      {expandedIndex !== null && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40" 
          onClick={() => setExpandedIndex(null)}
        ></div>
      )}
      
      <div className="container mx-auto px-4">
        {(title || description) && (
          <div className="mb-12 text-center">
            {title && <h2 className="">{title}</h2>}
            {description && <Paragraph text={description} maxChars={5000} classes=""/>}
          </div>
        )}

        <div className={`grid ${isMobile ? "grid-cols-1 gap-6" : "grid-cols-3 gap-x-8 gap-y-20"} relative`}>
          {awards.map((award, index) => (
            <div 
              key={index} 
              className={`
                flex relative bg-[#272726] flex-col p-6 lg:p-12 gap-8 shadow-[0px_24px_48px_-12px_rgba(0,0,0,0.18)]
                rounded-[4px] overflow-hidden hover:bg-[#FBF8F4] text-white hover:text-black transition-all
                ${isVisible ? 'fade-in-up' : 'opacity-0'} 
                ${expandedIndex === index ? 'z-50 bg-[#FBF8F4] !text-black' : 'z-30'}
              `}
              onClick={() => handleExpand(index)}
            >
              {award.image && (
                <div className="flex justify-between items-start relative w-full overflow-hidden">
                  <img 
                    src={award.image} 
                    alt={award.title} 
                    className="h-[56px] w-auto object-cover order-1"
                  />
                  {award.year && (
                    <span className={`${awardYearClass} order-2`}>{award.year}</span>
                  )}
                </div>
              )}
              <div className="flex-1 flex flex-col gap-y-2">
                <div className="flex flex-col mb-[32px] gap-y-[32px]">
                  <h3 className={`${awardTitleClass}`}>{award.title}</h3>
                </div>

                <div className={`flex flex-col ${awardTextClass}`}>
                  {award.description && (
                    <Paragraph text={expandedIndex === index ? award.description : award.description.slice(0, 120)} maxChars={expandedIndex === index ? 5000 : 120}/>
                  )}
                  {award.category && (
                    <Paragraph text={expandedIndex === index ? award.category : award.category.slice(0, 120)} maxChars={expandedIndex === index ? 5000 : 120} />
                  )}
                  {award.business_unit && (
                    <Paragraph text={award.business_unit} maxChars={120}/>
                  )}
                </div>

                {expandedIndex !== index && (
                  <div className="flex items-center">
                  <button 
                    className="justify-start flex mt-4 b_a1 items-center" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExpand(index);
                    }}
                  >
                    Read More

                  <i className="icon icon-right-arrow-alt ml-4 text-gray-500" />
                  </button>
                  </div>
                )}

                {expandedIndex === index && (
             
                  <div className="flex items-center">
                  <button 
                    className="justify-start flex mt-4 b_a1 items-center" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExpand(index);
                    }}
                  >
                    Close

                  <i className="icon icon-X ml-4 text-gray-500" />
                  </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Awards;
